import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
    StyleSheet,
    View,
    FlatList,
    ActivityIndicator,
    useWindowDimensions,
    TouchableOpacity,
    Text,
} from "react-native";
import { Card } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useTableHelper } from "../../../hooks/useTableHelper";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import { useSettingsContext } from '../../../hooks/useSettingsContext';
import { useAuthContext } from "../../../hooks/useAuthContext";
import { useCompany } from "../../../hooks/useCompany";
import { v4 as uuidv4 } from 'uuid';
import { showMessage } from "react-native-flash-message";
import HRAdminSeniorityLevelsScreen from "./HRAdminSeniorityLevelsScreen";
import Swal from "sweetalert2";

const StaffLevelsListView = React.memo(
    ({ item,
        index,
        handleEditPress,
        styles,
        t,
        handleDelete,
        d365,
        onNavigateToSeniorityLevels
    }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const toggleExpand = () => {
            setIsExpanded(!isExpanded);
        };
        return (
            <View style={styles.container}>
                <View style={styles.row}>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.hcode}
                            setValue={(text) => {
                                handleEditPress(index, item, "hcode", text);
                            }}
                            editable={true}
                            maxLen={4}
                            type="MAININPUT"
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.description}
                            setValue={(text) => {
                                handleEditPress(index, item, "description", text);
                            }}
                            editable={true}
                            maxLen={100}
                            type="MAININPUT"
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.horder !== undefined && item.horder !== null ? String(item.horder) : ""}
                            setValue={(text) => handleEditPress(index, item, "horder", text)}
                            editable={true}
                            maxLen={20}
                            type="MAININPUT"
                            keyboardType="numeric"
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.pdi_empl_level_code_EMPF}
                            setValue={(text) => {
                                handleEditPress(index, item, "pdi_empl_level_code_EMPF", text);
                            }}
                            editable={true}
                            maxLen={20}
                            type="MAININPUT"
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.pdi_empl_level_description_EMPF}
                            setValue={(text) => {
                                handleEditPress(index, item, "pdi_empl_level_description_EMPF", text);
                            }}
                            editable={true}
                            maxLen={20}
                            type="MAININPUT"
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.hcodeDME}
                            setValue={(text) => {
                                handleEditPress(index, item, "hcodeDME", text);
                            }}
                            editable={true}
                            maxLen={20}
                            type="MAININPUT"
                        />
                    </View>
                    <View>
                        <CustomButton
                            iconName="trash-outline"
                            onPress={() => handleDelete(item, index, "rowGuid")}
                            show="HOME_BELL"
                            type="HOME_BELL"
                            textTop="2"
                            sz={30}
                        />
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            {d365 && (
                                <Ionicons
                                    name={isExpanded ? "chevron-down-outline" : "chevron-forward-outline"}
                                    style={styles.icons}
                                    onPress={toggleExpand}
                                />
                            )}
                            <TouchableOpacity
                                onPress={() => {
                                    if (onNavigateToSeniorityLevels) {
                                        onNavigateToSeniorityLevels(item);
                                    }
                                }}
                                style={{ marginLeft: 10, padding: 5 }}
                                accessibilityLabel={t("common:openSeniorityLevels")}
                            >
                                <Ionicons name="ellipsis-vertical" size={24} color="#000" />
                            </TouchableOpacity>
                        </View>
                    </View>
                    {isExpanded && (
                        <View style={styles.item}>
                            <CustomInput
                                value={item.d365code}
                                setValue={(text) => {
                                    handleEditPress(index, item, "d365Code", text);
                                }}
                                editable={true}
                                maxLen={20}
                                type="MAININPUT"
                            />
                        </View>
                    )}
                </View>
            </View >
        );
    }
);

const HRAdminStaffLevelScreen = (props) => {
    const [staffLevels, setStaffLevels] = useState([]);
    const [originalStaffLevels, setOriginalStaffLevels] = useState([]);
    const [changes, setChanges] = useState({});
    const [deletedItems, setDeletedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const ref = useRef(null);
    const { t } = useTranslation();
    const { rowAdd, rowEdit, rowDelete } = useTableHelper();
    const { getCompanyStaffLevels, saveStaffLevels, updateStaffLevels, deleteStaffLevel } = useCompany();
    const { colors } = useTheme();
    const { width, height } = useWindowDimensions();
    const styles = useMemo(() => makeStyles(colors, width, height), [colors, width, height]);
    const common = createCommonStyles(colors);
    const { userId } = useAuthContext();
    const { showSeniorityLevels, setShowSeniorityLevels } = props;
    const [selectedStaffLevel, setSelectedStaffLevel] = useState(null);
    const { d365 } = useSettingsContext()

    const openSeniorityLevels = (staffLevel) => {
        setSelectedStaffLevel(staffLevel);
        setShowSeniorityLevels(!showSeniorityLevels);
    };

    const closeSeniorityLevels = () => {
        setShowSeniorityLevels(false);
        loadStaffLevels();
    };

    const loadStaffLevels = async () => {
        if (!props.selectedCompany) {
            setStaffLevels([]);
            setOriginalStaffLevels([]);
            return;
        }
        setIsLoading(true);
        try {
            const fetchedLevels = await getCompanyStaffLevels(props.selectedCompany);
            const fetchedLevelsWithGuid = fetchedLevels.map(item => ({
                ...item,
                horder: item.HOrder ?? item.horder ?? null,
                rowGuid: item.rowGuid || uuidv4(),
            }));
            setStaffLevels(fetchedLevelsWithGuid);
            setOriginalStaffLevels(fetchedLevelsWithGuid);
        } catch (error) {
            showMessage({
                message: t("common:ErrorLoadingStaffLevels"),
                type: "info",
                duration: 3000,
            });
            setStaffLevels([]);
            setOriginalStaffLevels([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadStaffLevels();
    }, [props.selectedCompany]);

    const removeFromChangesAfterSave = () => {
        setChanges({});
        setDeletedItems([]);
    };

    const handleEditPress = useCallback(
        (index, item, key, value) => {
            let sanitizedValue = value;
            if (key === "horder") {
                sanitizedValue = value.replace(/[^0-9]/g, '');
                const originalValue = item[key] !== undefined && item[key] !== null ? String(item[key]) : "";
                if (originalValue === sanitizedValue) return;
            } else {
                if (item[key] === sanitizedValue) return;
            }
            rowEdit(index, item, key, sanitizedValue, changes, setChanges, staffLevels, setStaffLevels);
        },
        [rowEdit, changes, setChanges, staffLevels, setStaffLevels]
    );

    const handleCancelChanges = () => {
        setStaffLevels(originalStaffLevels);
        setChanges({});
        setDeletedItems([]);
        showMessage({
            message: t("common:changesCancelled"),
            type: "info",
            duration: 3000,
        });
    };

    const validateInput = () => {
        for (const change of Object.values(changes)) {
            if (!change.hcode || change.hcode.trim() === "") {
                return [false, t("common:requiredLevel")];
            }
            if (!change.hcodeDME || change.hcodeDME.trim() === "") {
                return [false, t("common:requiredRegionalCode")];
            }
            if (
                change.horder !== null &&
                change.horder !== "" &&
                (Number(change.horder) < 1 || Number(change.horder) > 32767)
            ) {
                return [false, t("common:invalidOrder")];
            }
        }
        return [true, ""];
    };

    const handleSave = async () => {
        try {
            setIsLoading(true);

            // Handle deletions first
            if (deletedItems.length > 0) {
                for (const item of deletedItems) {
                    if (!item.hcode) {
                        continue;
                    }
                    try {
                        const deleteResult = await deleteStaffLevel(item.hcode, props.selectedCompany);
                        if (!deleteResult.success) {
                            if (deleteResult.conflict) {
                                const usedByData = deleteResult.details.usedBy || [];
                                let htmlContent = `
                                <p>${deleteResult.details.detail || "Staff level is in use."}</p>
                                <div style="max-height: 300px; overflow-y: auto; text-align: left; padding-left: 10px; border: 1px solid #ddd; border-radius: 4px;">`;
                                if (usedByData.length > 0) {
                                    htmlContent += `<ul style="margin: 0; padding-left: 20px;">`;
                                    usedByData.forEach(row => {
                                        htmlContent += `<li style="margin-bottom: 10px;">`;
                                        for (const [key, value] of Object.entries(row)) {
                                            htmlContent += `<strong>${key}:</strong> ${value} <br/>`;
                                        }
                                        htmlContent += `</li>`;
                                    });
                                    htmlContent += `</ul>`;
                                }
                                htmlContent += `</div>`;
                                await Swal.fire({
                                    icon: "warning",
                                    title: deleteResult.details.title || "Cannot delete staff level",
                                    html: htmlContent,
                                    confirmButtonText: "OK",
                                    width: '600px',
                                });
                                setIsLoading(false);
                                return;
                            } else {
                                showMessage({ message: t("common:failedDelete"), type: "danger", duration: 3000 });
                                setIsLoading(false);
                                return;
                            }
                        }
                    } catch (err) {
                        console.error("Error deleting staff level:", err);
                        showMessage({ message: t("common:failedDelete"), type: "danger", duration: 3000 });
                        setIsLoading(false);
                        return;
                    }
                }
                showMessage({ message: t("common:staffSuccessDelete"), type: "success", duration: 3000 });
                removeFromChangesAfterSave();
            }

            // Handle additions and updates
            if (Object.entries(changes).length > 0) {
                const [isValid, validationMessage] = validateInput();
                if (!isValid) {
                    showMessage({
                        message: validationMessage,
                        type: "danger",
                        duration: 3000,
                    });
                    setIsLoading(false);
                    return;
                }

                // Separate new and existing changes
                const newItems = [];
                const existingItems = [];
                for (const change of Object.values(changes)) {
                    if (!originalStaffLevels.find(s => s.rowGuid === change.rowGuid)) {
                        newItems.push(change);
                    } else {
                        existingItems.push(change);
                    }
                }

                // Save new items
                for (const newItem of newItems) {
                    const levelToSave = {
                        Company: props.selectedCompany,
                        HCode: newItem.hcode?.trim() ?? "",
                        Description: newItem.description ?? "",
                        HOrder: newItem.horder ?? null,
                        InterfaceCode: newItem.pdi_empl_level_code_EMPF ?? null,
                        InterfaceDescription: newItem.pdi_empl_level_description_EMPF ?? null,
                        RegionalCode: newItem.hcodeDME ?? null,
                    };
                    try {
                        const saveResult = await saveStaffLevels(levelToSave);
                        if (!saveResult) {
                            showMessage({
                                message: t("common:failedSave"),
                                type: "danger",
                                duration: 3000,
                            });
                            setIsLoading(false);
                            return;
                        }
                    } catch (error) {
                        console.error("Error saving new staff level:", error);
                        showMessage({
                            message: t("common:failedSave"),
                            type: "danger",
                            duration: 3000,
                        });
                        setIsLoading(false);
                        return;
                    }
                }


                // Update existing items
                for (const existingItem of existingItems) {
                    const levelToUpdate = {
                        Company: props.selectedCompany,
                        HCode: existingItem.hcode.trim(),
                        OriginalHCode: originalStaffLevels.find(s => s.rowGuid === existingItem.rowGuid)?.hcode,
                        Description: existingItem.description ?? "",
                        HOrder: existingItem.horder ?? null,
                        InterfaceCode: existingItem.pdi_empl_level_code_EMPF ?? null,
                        InterfaceDescription: existingItem.pdi_empl_level_description_EMPF ?? null,
                        RegionalCode: existingItem.hcodeDME ?? null,
                        RowGuid: existingItem.rowguid ?? null,
                    };
                    try {
                        const updateResult = await updateStaffLevels(levelToUpdate);
                        if (!updateResult) {
                            showMessage({
                                message: t("common:failedUpdate"),
                                type: "danger",
                                duration: 3000,
                            });
                            setIsLoading(false);
                            return;
                        }
                    } catch (error) {
                        console.error("Error updating staff level:", error);
                        showMessage({
                            message: t("common:failedUpdate"),
                            type: "danger",
                            duration: 3000,
                        });
                        setIsLoading(false);
                        return;
                    }
                }

                showMessage({
                    message: t("common:staffSuccessSave"),
                    type: "success",
                    duration: 3000,
                });
                removeFromChangesAfterSave();
            }

            await loadStaffLevels();
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            if (error.response) {
                console.error("Save API error:", error.response.data);
                showMessage({
                    message: error.response.data.title || t("common:errorSave"),
                    description: JSON.stringify(error.response.data.errors || error.response.data),
                    type: "danger",
                    duration: 5000,
                });
            } else {
                console.error("Error in save/delete operations:", error);
                showMessage({
                    message: t("common:errorSave"),
                    type: "danger",
                    duration: 3000,
                });
            }
        }
    };

    const handleDelete = (item, index) => {
        setDeletedItems(prev => [...(prev || []), item]);
        rowDelete(item, index, "rowGuid", changes, setChanges, staffLevels, setStaffLevels, setDeletedItems, ref);
    };

    const renderData = useCallback((item, index) => {
        return (
            <Card style={[common.cardstyle]} >
                <Card.Content>
                    <StaffLevelsListView
                        item={item}
                        index={index}
                        handleEditPress={handleEditPress}
                        styles={styles}
                        t={t}
                        handleDelete={handleDelete}
                        d365={d365}
                        onNavigateToSeniorityLevels={openSeniorityLevels}
                    />
                </Card.Content>
            </Card>
        )
    }, [staffLevels, changes, deletedItems]);

    if (isLoading) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        );
    }

    if (showSeniorityLevels) {
        return (
            <View style={styles.mainView}>
                <HRAdminSeniorityLevelsScreen
                    selectedCompany={props.selectedCompany}
                    hcode={selectedStaffLevel?.hcode}
                    onClose={closeSeniorityLevels}
                />
            </View>
        );
    }

    return (
        <View style={styles.mainView}>
            {showSeniorityLevels ? (
                // Show Seniority Levels Screen
                <HRAdminSeniorityLevelsScreen
                    selectedCompany={props.selectedCompany}
                    onClose={closeSeniorityLevels}
                />
            ) : (
                <View style={styles.container}>
                    <View style={styles.buttonsRow}>
                        <View style={styles.leftButtons}>
                        </View>
                        <View style={styles.rightButtons}>
                            <CustomButton
                                text={t("common:new")}
                                onPress={() => {
                                    rowAdd(
                                        staffLevels,
                                        setStaffLevels,
                                        ["rowGuid"],
                                        [],
                                        ref,
                                        t,
                                        [],
                                        [],
                                        changes,
                                        setChanges,
                                        undefined,
                                        undefined,
                                        "rowGuid",
                                        false,
                                        ["date"],
                                        () => uuidv4(),
                                        { isNew: true }
                                    );
                                }}
                                type="BUTTON_ADD"
                                textTop="0"
                                sz={15}
                            />
                            {(Object.keys(changes).length > 0 ||
                                deletedItems.length > 0) && (
                                    <CustomButton
                                        text={t("common:saveFunction")}
                                        onPress={handleSave}
                                        type="BUTTON_SAVE"
                                        isGradient={true}
                                        textTop="0"
                                        sz={15}
                                    />
                                )}
                            <CustomButton
                                text={t("common:cancel")}
                                onPress={handleCancelChanges}
                                type="BUTTON_CANCEL"
                                textTop="0"
                                sz={15}
                            />
                        </View>
                    </View>
                    <FlatList
                        data={staffLevels}
                        keyExtractor={(item) => String(item.rowGuid)}
                        renderItem={({ item, index }) =>
                            renderData(item, index)}
                        refreshing={isLoading}
                        ref={ref}
                        ListHeaderComponent={
                            <View style={common.listTitle}>
                                <View style={styles.row}>
                                    <CustomLabel value={t("common:levelName")} type="SUBTITLE" />
                                    <CustomLabel value={t("common:description")} type="SUBTITLE" />
                                    <CustomLabel value={t("common:order")} type="SUBTITLE" />
                                    <CustomLabel value={t("common:interfaceCode")} type="SUBTITLE" />
                                    <CustomLabel value={t("common:interfaceDescription")} type="SUBTITLE" />
                                    <CustomLabel value={t("common:regionalCode")} type="SUBTITLE" />


                                    {/* <CustomLabel value={t("common:d365Code")} type="SUBTITLE" />  */}

                                    <View style={{ width: 40 }} />
                                </View>
                                <View style={common.divider} />
                            </View>
                        }
                        ListEmptyComponent={
                            isLoading ? (
                                <ActivityIndicator animating={true} color={colors.blue} />
                            ) : (
                                <CustomLabel
                                    value={t("common:noStaffDataPreview")}
                                    type="NO_FUNCTIONS"
                                />
                            )
                        }
                        initialNumToRender={4}
                    />
                </View>
            )}
        </View>
    );
}

const makeStyles = (colors, width) => StyleSheet.create({
    container: {
        flex: 1,
    },
    mainView: {
        flex: 1,
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
    },
    item: {
        flex: 1,
        justifyContent: "center",
        marginRight: 6,
    },
    buttonsRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginHorizontal: 15,
        marginVertical: 10,
    },
    leftButtons: {
        flexDirection: "row",
        alignItems: "center",
    },
    rightButtons: {
        flexDirection: "row",
        alignItems: "center",
    },
    loader: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "4%",
    },
});

export default HRAdminStaffLevelScreen;